import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

const getIsOnline = () =>
    typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean' ? navigator.onLine : true;

const OfflineBanner = () => {
    const location = useLocation();
    const [isOnline, setIsOnline] = useState<boolean>(getIsOnline);
    const [dismissed, setDismissed] = useState(false);
    const bannerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleOnline = () => { setIsOnline(true); setDismissed(false); };
        const handleOffline = () => { setIsOnline(false); setDismissed(false); };
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    // Publish banner height so mobile TopBar can shift down and stay accessible
    useLayoutEffect(() => {
        if (!bannerRef.current) return;
        const obs = new ResizeObserver(([entry]) => {
            document.documentElement.style.setProperty('--offline-banner-height', `${entry.contentRect.height}px`);
        });
        obs.observe(bannerRef.current);
        return () => {
            obs.disconnect();
            document.documentElement.style.setProperty('--offline-banner-height', '0px');
        };
    }, []);

    const isReportsRoute = (location.pathname || '').startsWith('/reports');
    if (!isReportsRoute || isOnline || dismissed) return null;

    return (
        <div
            ref={bannerRef}
            role="status"
            aria-live="polite"
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 9999,
                background: '#FDF1E1',
                color: 'var(--t1, #1A1D2E)',
                fontFamily: 'var(--font-primary)',
                fontSize: 'var(--fs-body, 13px)',
                fontWeight: 500,
                borderBottom: '1px solid var(--orange-soft, rgba(217,119,6,0.08))',
                boxShadow: 'var(--topbar-shadow, 0 1px 3px rgba(0,0,0,0.04))',
                display: 'flex',
                alignItems: 'center',
                padding: 'max(8px, env(safe-area-inset-top)) 12px 8px 16px',
                gap: 8,
            }}
        >
            <span style={{ flex: 1, textAlign: 'center' }}>You're offline. Data shown may not be current.</span>
            <button
                type="button"
                onClick={() => setDismissed(true)}
                aria-label="Dismiss offline banner"
                style={{
                    flexShrink: 0,
                    width: 22,
                    height: 22,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    borderRadius: 4,
                    color: 'var(--t2, #6B7280)',
                    fontSize: 16,
                    lineHeight: 1,
                    padding: 0,
                }}
            >
                ✕
            </button>
        </div>
    );
};

export default OfflineBanner;
